import React, { useState, useEffect, useCallback, useContext, useMemo , useRef} from 'react';
import _ from 'lodash';
import PropTypes from 'prop-types';
import appConfig from 'app-config';
import classNames from 'classnames';
import { useTranslator } from '@jutro/locale';
import { ViewModelServiceContext, ViewModelForm, withViewModelService } from '@xengage/gw-portals-viewmodel-react';
import { withAuthenticationContext } from '@xengage/gw-digital-auth-react';
import { MLSearchDataHolderService, MLSearchTypesService } from 'ml-services-common-react'
import { useValidation } from '@xengage/gw-portals-validation-react';
import metadata from './MLSearchBarComponent.metadata.json5';

import styles from './MLSearchBarComponent.module.scss';
import { DropdownSelectField } from '@jutro/legacy/components';
import { useAuthentication } from '@xengage/gw-digital-auth-react';
import { MLPortalSettingsService } from 'gw-capability-quoteandbind';
import messages from '../../../../capabilities/ml-services-common-react/services/MLSearchTypesService.messages';


const MLSearchBarComponent = (props) => {
    const { history, authUserData } = props;
    const { MLUserTypes } = appConfig;
    const { authHeader } = useAuthentication();

    const translator = useTranslator();
    const viewModelService = useContext(ViewModelServiceContext);   
    const initSearchRequestVM = viewModelService.create(
        {
            searchParam: '',
            name_ML: '',
            zipCode_ML: undefined,
            state_ML: '',
            country_ML: 'US',
            selectedSearchType: {}
        },
        'pc', 'edge.capabilities.gateway.search.dto.SearchRequestDTO'
    );
 
    const [searchRequestVM, updateSearchRequestVM] = useState(initSearchRequestVM);
    const [requestObj, updateRequestObj] = useState(initSearchRequestVM.value);
    const [popoverVisible, updatePopoverVisible]= useState(false);
    const availableSearchTypes = useRef([]);
    const zipCodeRef = useRef(null);
    const { onValidate } = useValidation('MLSearchBarComponent');

    const [isPortalSolrSingleTextSearchEnabled, setIsPortalSolrSingleTextSearchEnabled] = useState(false);

    useEffect(() => {
        async function getPortalSettings() {

            MLPortalSettingsService.getSettings(authHeader).then((response) => {
                if (response) {
                    if (response.enablePortalSolrSingleTextSearch) {
                        setIsPortalSolrSingleTextSearchEnabled(true);
                    }
                }
            });
        }

        getPortalSettings();
    }, []);

    const writeValue = useCallback(
        (value, path) => {
            const newSearchRequestVM = viewModelService.clone(searchRequestVM);
            _.set(newSearchRequestVM, path, value);
            updateSearchRequestVM(newSearchRequestVM);
        },
        [searchRequestVM, viewModelService]
    )

    const resetAvailableSearchTypesName = useCallback((nameType) => {
        if(nameType === 'short') {
            availableSearchTypes.current.forEach((type) => {
                type.name = type.shortName;
            })
        } else {
            availableSearchTypes.current.forEach((type) => {
                type.name = type.longName;
            })
        }       
    }, [])

    const resetSelectedTypeName = useCallback(() => {
        if (requestObj.selectedSearchType.name === requestObj.selectedSearchType.shortName) {
            resetAvailableSearchTypesName();
        }
        const newRequestObj = _.cloneDeep(requestObj);
        newRequestObj.selectedSearchType.name = requestObj.selectedSearchType.longName;
        updateRequestObj(newRequestObj);
    }, [resetAvailableSearchTypesName, requestObj])

    const trimNameToShortName = useCallback(() => {
        resetAvailableSearchTypesName('short');
        const newRequestObj = _.cloneDeep(requestObj);
        newRequestObj.selectedSearchType.name = requestObj.selectedSearchType.shortName;
        updateRequestObj(newRequestObj);
    }, [resetAvailableSearchTypesName, requestObj])

    const updateRequestSearchType = useCallback((typeCode) => {
        const newRequestObj = _.cloneDeep(requestObj);
        const newType = availableSearchTypes.current.find((type) => type.code === typeCode)
        newRequestObj.selectedSearchType = newType;
        MLSearchDataHolderService.setSearchType(newType);
        MLSearchDataHolderService.setSearchTypeCode(newType ? newType.code : undefined);
        MLSearchDataHolderService.setSearchTypeChanged(true);
        newRequestObj.selectedSearchType.name = newType.shortName;
        updateRequestObj(newRequestObj);
    }, [requestObj])

    const setSelectedSearchType = useCallback((typeCode) => {
        resetAvailableSearchTypesName();
        updateRequestSearchType(typeCode);
    }, [resetAvailableSearchTypesName, updateRequestSearchType])

    const isCompNameSearch = () => {
        return MLSearchTypesService.isSearchTypeCompRaterQuoteName(requestObj.selectedSearchType.code);
    }

    const isSearchAcctNameSearch = () => {
        return MLSearchTypesService.isSearchTypeAcctName(requestObj.selectedSearchType.code);
    }

    const isJobAndZipCodeSearch = () => {
        return MLSearchTypesService.isSearchTypeCompRaterJobZipcode(requestObj.selectedSearchType.code);
    }

    const isInvalidEQS = () => !(searchRequestVM.searchParam.value && searchRequestVM.zipCode_ML.value);

    const isEQSAndFieldsMissing = () =>{
        return isJobAndZipCodeSearch() && isInvalidEQS();
    } 

    const isEQSAvailable = () => {
        if (authUserData) {
            return authUserData.userType === MLUserTypes.IA || authUserData.userType === MLUserTypes.PCS || authUserData.userType === MLUserTypes.MET_GA;
        }

        return false;
    }

    const fetchPortalSettings = async () => {
        const settings = await MLPortalSettingsService.getSettings(authHeader);
        return settings && settings?.enablePortalSolrSearch;
    };

    const fetchPortalSingleTextSettings = async () => {
        const settings = await MLPortalSettingsService.getSettings(authHeader);

        return settings && settings?.enablePortalSolrSingleTextSearch;
    };


    const updateNamePlaceholder = (searchTypes) => {
        const SEARCH_TYPE_ACCT_NAME = 'acctname';
        const accountSearchType = searchTypes.find((type) => type.code === SEARCH_TYPE_ACCT_NAME);
        accountSearchType.placeholder = messages.solrLastNameFirstName;
    };

    const initOrRefreshSearchTypes = async () => {
        const solrEnabled = await fetchPortalSettings();

        const initSearchTypes = MLSearchTypesService.getSearchTypes().filter((type) => {
            if (MLSearchTypesService.isSearchTypeCompRaterJobZipcode(type.code)) {
                return isEQSAvailable(); 
            }

            return type.isAvailable(); 
        });
        
        if (solrEnabled) {
            updateNamePlaceholder(initSearchTypes);
        }

        initSearchTypes.forEach((type) => {
            type.name = type.longName;
        });

        availableSearchTypes.current = initSearchTypes.map((type) => {
            const translatedBaseTypes =  { ...type,
                longName: _.toUpper(translator(type.longName)),
                shortName: _.toUpper(translator(type.shortName)),
                placeholder: translator(type.placeholder)
            }
            return _.isUndefined(type.placeholderSecond) ? translatedBaseTypes : {...translatedBaseTypes, placeholderSecond: translator(type.placeholderSecond)}
        });

        const selectedTypeFromDataHolder = MLSearchDataHolderService.getSearchType();
        const selectedSearchType = selectedTypeFromDataHolder
            ? availableSearchTypes.current.find((type) => type.code === selectedTypeFromDataHolder.code)
            : availableSearchTypes.current.find((type) => MLSearchTypesService.isSearchTypeAcctName(type.code));
        updateRequestSearchType(selectedSearchType.code);
    }
    
    useEffect(() => {
        initOrRefreshSearchTypes();
    }, [])

    const clearInputs = useCallback(() => {
        const newSearchRequestVM = viewModelService.clone(searchRequestVM);
        _.set(newSearchRequestVM, 'searchParam.value', undefined);
        _.set(newSearchRequestVM, 'name_ML.value', undefined);
        _.set(newSearchRequestVM, 'state_ML.value', undefined);
        _.set(newSearchRequestVM, 'zipCode_ML.value', undefined);
        updateSearchRequestVM(newSearchRequestVM);
    }, [searchRequestVM, viewModelService])

    const onBlurZipcode = useCallback(() => {
        let newVal = zipCodeRef.current.props.value;
        if (!_.isUndefined(newVal) && !newVal.includes('-') && newVal.length > 5) {
            const first5Digits = newVal.substring(0, 5);
            const last4Digits = newVal.substring(5);
            newVal = `${first5Digits}-${last4Digits}`;
            writeValue(newVal, 'zipCode_ML.value');
        } 
    }, [writeValue])

    const onChangeSearchType = useCallback((type) => {
        if (!_.isEmpty(type)) {
            setSelectedSearchType(type);
            clearInputs();
        }
        
    }, [setSelectedSearchType, clearInputs])

    const performSearch = () => {
        MLSearchDataHolderService.setSearchBtnClicked(true);
        const param = searchRequestVM.searchParam.value || searchRequestVM.name_ML.value;
        const searchType = MLSearchDataHolderService.getSearchType();
        if (param || (searchType && searchType.code === 'EQS' && searchRequestVM.zipCode_ML.value)) {
            clearInputs();
            const forceReloadUniqueId = Date.now() + Math.random();
            history.push(`/search/${param}/${forceReloadUniqueId}`);
        }
    }

    const onSearchButtonClick = () => {
        const isValid = searchRequestVM.aspects.valid && searchRequestVM.aspects.subtreeValid;
        const isEQSSearch = isJobAndZipCodeSearch();
        if (!isValid || (isEQSSearch && isInvalidEQS())) {
            return;
        }
        MLSearchDataHolderService.setSearchInput(searchRequestVM.searchParam.value || searchRequestVM.name_ML.value);
        if (isJobAndZipCodeSearch()) {
            if (_.isUndefined(searchRequestVM.zipCode_ML.value)) {
                MLSearchDataHolderService.setZipCode('');
            } else {
                MLSearchDataHolderService.setZipCode(searchRequestVM.zipCode_ML.value);
            }
        }
        performSearch();
    }

    const applyFilterAndSearch = () => {
        updatePopoverVisible(false);
        if (!_.isUndefined(searchRequestVM.state_ML.value)) {
            MLSearchDataHolderService.setState(searchRequestVM.state_ML.value.code);
        }
        MLSearchDataHolderService.setZipCode(searchRequestVM.zipCode_ML.value);
        onSearchButtonClick();
    }

    const getAvailableStates = useMemo(() => {
        const states = initSearchRequestVM.state_ML.aspects.availableValues;
        return states.map((type) => {
            return {
                code: type.code,
                name: translator({
                    id: type.name,
                    defaultMessage: type.name
                })
            };
        });
    }, [initSearchRequestVM.state_ML.aspects.availableValues, translator]);

    const getSelectedOptionDisplay = () => {
        return (           
            <div 
                onMouseEnter={resetSelectedTypeName} 
                onMouseLeave={trimNameToShortName} 
            >
                <DropdownSelectField
                    id="selectedOption"
                    onValueChange={onChangeSearchType}
                    value={requestObj.selectedSearchType.code}
                    availableValues={availableSearchTypes.current}
                    alwaysShowPlaceholder={false}
                    searchable
                />
            </div>
        )
    }

    const submitSearchButtonProps = {
        title: 'Submit Search',
        disabled: isEQSAndFieldsMissing()
    };
    
    // Apply keyboard control to filter tooltip
    useEffect(() => {
        const tooltip = document.getElementById('tooltipPopOver')
        const applyFilterButton = document.getElementById('applySearchButton');

        if(popoverVisible) {
            document.getElementsByClassName('zip-code-filter-field')[0].focus();
        }
        
        if (tooltip) {
            tooltip.onkeyup = (evt) => {
                if (evt.key === 'Escape') {
                    updatePopoverVisible(false);
                }
            }
        }
        
        if (applyFilterButton) {
            applyFilterButton.onkeydown = (evt) => {
                if(evt.key === 'Tab' && !evt.shiftKey) {
                    updatePopoverVisible(false);
                }
            }
        }
    }, [popoverVisible])

    const overrideProps = {
        selectedOptionContainer: {
            content: getSelectedOptionDisplay()
        },
        searchContainer :{
            visible:isPortalSolrSingleTextSearchEnabled === false
        },
        solarSearchContainer:{
            visible:isPortalSolrSingleTextSearchEnabled === true
        },
        solarSearchRequestModel: {
            path: `${requestObj.selectedSearchType.model}.value`,
            value: _.get(searchRequestVM, `${requestObj.selectedSearchType.model}.value`),
            placeholder: 'Search for Name, Policy, Quote, Address, or Phone',
            maxLength: requestObj.selectedSearchType.maxLength,
            className: isJobAndZipCodeSearch() ? classNames(styles['pull-left'], 'eqs-ctrl', 'eqs-submission') : 'solarSearchRequestModel solarSearchRequestModel-media'
        },
        solarSearchRequestModelSecond: {
            visible: isJobAndZipCodeSearch(),
            path: `${requestObj.selectedSearchType.modelSecond}.value`,
            value: _.get(searchRequestVM, `${requestObj.selectedSearchType.modelSecond}.value`),
            placeholder: 'Search for Name, Policy#, Quote#, Address, or Phone#',
            maxLength: requestObj.selectedSearchType.maxLengthSecond,
            className: classNames(styles['pull-left'], 'eqs-ctrl', 'eqs-zipcode')
        },
        requestModel: {
            path: `${requestObj.selectedSearchType.model}.value`,
            value: _.get(searchRequestVM, `${requestObj.selectedSearchType.model}.value`),
            placeholder: requestObj.selectedSearchType.placeholder,
            maxLength: requestObj.selectedSearchType.maxLength,
            className: isJobAndZipCodeSearch() ? classNames(styles['pull-left'], 'eqs-ctrl', 'eqs-submission') : ''
        },
        requestModelSecond: {
            visible: isJobAndZipCodeSearch(),
            path: `${requestObj.selectedSearchType.modelSecond}.value`,
            value: _.get(searchRequestVM, `${requestObj.selectedSearchType.modelSecond}.value`),
            placeholder: requestObj.selectedSearchType.placeholderSecond,
            maxLength: requestObj.selectedSearchType.maxLengthSecond,
            className: classNames(styles['pull-left'], 'eqs-ctrl', 'eqs-zipcode')
        },
        searchFilterTooltipContainer: {
            visible: isCompNameSearch() || isSearchAcctNameSearch()
        },
        tooltipButton: {
            onClick: () => { updatePopoverVisible(!popoverVisible) },
            mlTagManagerLinkClick: "AgentSearchFilter",
            title: 'Filter'
        },
        applySearchButton: {
            mlTagManagerLinkClick: "Agentsearch_applyFilter"
        },
        tooltipPopOver: {
            visible: popoverVisible,
        },
        zipcodeField: {
            ref: zipCodeRef,
            onBlur: onBlurZipcode,
            value: _.get(searchRequestVM, 'zipCode_ML.value'),
            showErrors: !searchRequestVM.zipCode_ML.aspects.valid || !searchRequestVM.zipCode_ML.aspects.subtreeValid,
            validationMessages: searchRequestVM.zipCode_ML.aspects.validationMessages
        },
        stateField: {
            value: _.get(searchRequestVM, 'state_ML.value'),
            availableValues: getAvailableStates
        },
        submitSearchButton: submitSearchButtonProps
    };

    const resolvers = {
        resolveCallbackMap: {
            onSearchButtonClick,
            writeValue,
            applyFilterAndSearch
        },
        resolveClassNameMap: styles
    };

    return <ViewModelForm
        uiProps={metadata.componentContent}
        model={searchRequestVM}
        overrideProps={overrideProps}
        onValidationChange={onValidate}
        classNameMap={resolvers.resolveClassNameMap}
        callbackMap={resolvers.resolveCallbackMap}
    />
};

MLSearchBarComponent.propTypes = {
    history: PropTypes.shape({
        push: PropTypes.func,
        location: PropTypes.shape({
            pathname: PropTypes.string
        })
    }),
    authUserData: PropTypes.shape({
        displayName: PropTypes.string,
        userType: PropTypes.string,
        primaryProducerCode_ML: PropTypes.shape({
            producerType_ML: PropTypes.string
        })
    })
};

export default withAuthenticationContext(withViewModelService(MLSearchBarComponent));
